import { useEffect, useRef, useState } from 'react';
import { ActionIcon, Badge, Button, Group, Paper, Stack, Text, TextInput } from '@mantine/core';
import { IconSearch, IconUserPlus, IconX } from '@tabler/icons-react';
import { getCustomer, listCustomers, type CustomerSummary } from '../api-client.ts';
import { tokens } from '../ui/tokens.ts';
import { CustomerFormModal } from './CustomerFormModal.tsx';

interface Props {
  readonly venueId: string;
  readonly selected: CustomerSummary | null;
  readonly onSelect: (c: CustomerSummary | null) => void;
}

function contactPreview(c: CustomerSummary): string {
  const m = c.contactMethods[0];
  return m ? `${m.kind}: ${m.value}` : '（無聯絡方式）';
}

export function CustomerPicker({ venueId, selected, onSelect }: Props): JSX.Element {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<CustomerSummary[]>([]);
  const [searching, setSearching] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (selected) return;
    if (search.trim().length === 0) {
      setResults([]);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearching(true);
      void listCustomers({ venueId, search: search.trim(), limit: 8 })
        .then((r) => setResults(r.items.map((it) => it.customer)))
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 250);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search, selected, venueId]);

  if (selected) {
    return (
      <Paper withBorder p="sm" radius="sm" data-testid="customer-picker-selected">
        <Group justify="space-between" wrap="nowrap">
          <Stack gap={2}>
            <Text size="sm" fw={600}>
              {selected.displayName}
            </Text>
            <Text size="xs" c="dimmed">
              {contactPreview(selected)}
            </Text>
            {selected.tags.length > 0 && (
              <Group gap={4}>
                {selected.tags.map((t) => (
                  <Badge
                    key={t}
                    size="xs"
                    variant="outline"
                    color={tokens.color.mantine['primary']}
                  >
                    {t}
                  </Badge>
                ))}
              </Group>
            )}
          </Stack>
          <ActionIcon
            variant="subtle"
            onClick={() => onSelect(null)}
            aria-label="移除客戶連結"
          >
            <IconX size={16} />
          </ActionIcon>
        </Group>
      </Paper>
    );
  }

  return (
    <Stack gap={4}>
      <Group gap={4} align="end">
        <TextInput
          flex={1}
          label="連結客戶（選填）"
          placeholder="輸入姓名或電話搜尋"
          leftSection={<IconSearch size={14} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          data-testid="customer-picker-search"
        />
        <Button
          variant="light"
          color={tokens.color.mantine['primary']}
          leftSection={<IconUserPlus size={14} />}
          onClick={() => setCreateOpen(true)}
        >
          新增
        </Button>
      </Group>
      {searching && (
        <Text size="xs" c="dimmed">
          搜尋中…
        </Text>
      )}
      {!searching && search.trim().length > 0 && results.length === 0 && (
        <Text size="xs" c="dimmed">
          找不到符合的客戶，改用「新增」建立。
        </Text>
      )}
      {results.length > 0 && (
        <Paper withBorder radius="sm">
          <Stack gap={0}>
            {results.map((c) => (
              <Group
                key={c.customerId}
                justify="space-between"
                wrap="nowrap"
                p="xs"
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  onSelect(c);
                  setSearch('');
                  setResults([]);
                }}
                data-testid="customer-picker-result"
              >
                <Stack gap={0}>
                  <Text size="sm">{c.displayName}</Text>
                  <Text size="xs" c="dimmed">
                    {contactPreview(c)}
                  </Text>
                </Stack>
                {c.tags.length > 0 && (
                  <Badge
                    size="xs"
                    variant="outline"
                    color={tokens.color.mantine['primary']}
                  >
                    {c.tags[0]}
                  </Badge>
                )}
              </Group>
            ))}
          </Stack>
        </Paper>
      )}

      <CustomerFormModal
        opened={createOpen}
        onClose={() => setCreateOpen(false)}
        onSaved={(id) => {
          setCreateOpen(false);
          void getCustomer(id).then((c) => {
            if (c) onSelect(c);
          });
        }}
        venueId={venueId}
        existing={null}
      />
    </Stack>
  );
}
